import { Head, Link, router } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { SubscriptionStatusBadge } from "@/components/SubscriptionStatusBadge"
import { Select } from "@/components/ui/select"

interface SubscriptionRow {
  id: number
  account_id: number
  account_name: string
  owner_email: string | null
  stripe_subscription_id: string
  status: string
  quantity: number
  pending_quantity: number | null
  cancel_at_period_end: boolean
  current_period_end: string | null
}

export default function AdminSubscriptionsIndex({
  subscriptions,
  status,
  statuses,
}: {
  subscriptions: SubscriptionRow[]
  status: string
  statuses: string[]
}) {
  const filter = (value: string) => {
    router.get("/admin/subscriptions", value ? { status: value } : {}, { preserveState: true })
  }

  return (
    <>
      <Head title="Subscriptions">
        <meta name="description" content="All Flywrite subscriptions mirrored from Stripe." />
        <meta property="og:title" content="Subscriptions" />
        <meta property="og:description" content="All Flywrite subscriptions mirrored from Stripe." />
      </Head>
      <AdminShell>
        <PageHeader
          title="Subscriptions"
          description={`${subscriptions.length} ${subscriptions.length === 1 ? "subscription" : "subscriptions"}${status ? ` with status “${status}”` : ""}.`}
          actions={
            <Select
              value={status}
              onChange={(e) => filter(e.target.value)}
              aria-label="Filter by status"
              className="min-w-40"
            >
              <option value="">All statuses</option>
              {statuses.map((s) => (
                <option key={s} value={s}>
                  {s.replace(/_/g, " ")}
                </option>
              ))}
            </Select>
          }
        />

        {subscriptions.length === 0 ? (
          <p className="mt-8 text-sm text-ink-muted">No subscriptions found.</p>
        ) : (
          <div className="mt-6 overflow-x-auto rounded-lg border border-hairline">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline bg-surface text-left">
                  <th className="px-4 py-3 font-medium text-ink-muted">Account</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Owner</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Status</th>
                  <th className="px-4 py-3 text-right font-medium text-ink-muted">Seats</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Period ends</th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.map((sub) => (
                  <tr key={sub.id} className="border-b border-hairline last:border-0">
                    <td className="px-4 py-3">
                      <Link href={`/admin/accounts/${sub.account_id}`}>{sub.account_name}</Link>
                    </td>
                    <td className="px-4 py-3">{sub.owner_email ?? "—"}</td>
                    <td className="px-4 py-3">
                      <SubscriptionStatusBadge status={sub.status} />
                      {sub.cancel_at_period_end && (
                        <span className="ml-2 text-xs text-ink-muted">cancels at period end</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {sub.quantity}
                      {sub.pending_quantity != null && (
                        <span className="text-xs text-ink-muted"> → {sub.pending_quantity}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {sub.current_period_end ? formatDate(sub.current_period_end) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminShell>
    </>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}
